/**
 * Oil & Gas — Facility Safety & Flaring
 *
 * Reads OGI/thermal survey missions with completed AI analysis and surfaces:
 *   • Thermal baseline tracking per corridor
 *   • Flare intensity scoring from satellite-derived radiative power
 *   • Asset safety heatmap summary (anomaly count per facility)
 *   • Incident trend cards showing thermal events over time
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Flame, Thermometer, ShieldAlert, BarChart2,
  RefreshCw, ChevronDown, ChevronUp, AlertTriangle,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import api from '../../services/api';
import { satelliteBoundariesApi } from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FacilityMission {
  id: string;
  name: string;
  status: string;
  created_at: string;
  updated_at: string;
  metadata?: {
    vertical?: string;
    ai_analysis_status?: string;
    corridor_name?: string;
    thermal_anomaly_c?: number;
    flare_radiative_power_mw?: number;
    flare_intensity_score?: number;   // 0–100
    baseline_temp_c?: number;
    peak_temp_c?: number;
    asset_type?: string;              // 'wellhead' | 'storage_tank' | 'compressor' | 'pipeline' | 'flare_stack'
    diagnosis?: string;
    severity?: string;
    [key: string]: any;
  };
}

interface Corridor {
  id: string;
  name: string;
  metadata?: { vertical?: string };
}

// ─── Flare intensity gauge ────────────────────────────────────────────────────

function FlareGauge({ score }: { score: number }) {
  const pct = Math.min(100, Math.max(0, score));
  const colour =
    score >= 80 ? 'bg-red-500' :
    score >= 60 ? 'bg-orange-500' :
    score >= 30 ? 'bg-amber-400' :
    'bg-green-500';
  const label =
    score >= 80 ? 'Severe' :
    score >= 60 ? 'High' :
    score >= 30 ? 'Moderate' :
    'Normal';
  return (
    <div className="flex items-center gap-2 mt-1">
      <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
        <div className={`h-full rounded-full ${colour}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 w-16 text-right">{label}</span>
    </div>
  );
}

// ─── Asset type label ─────────────────────────────────────────────────────────

const ASSET_LABELS: Record<string, string> = {
  wellhead:     'Wellhead',
  storage_tank: 'Storage Tank',
  compressor:   'Compressor Station',
  pipeline:     'Pipeline',
  flare_stack:  'Flare Stack',
};

// ─── Component ────────────────────────────────────────────────────────────────

export const FacilitySafetyPage: React.FC = () => {
  const [missions, setMissions]       = useState<FacilityMission[]>([]);
  const [corridors, setCorridors]     = useState<Corridor[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [expandedId, setExpandedId]   = useState<string | null>(null);
  const [filterCorridor, setFilterCorridor] = useState('');
  const [filterAsset, setFilterAsset]       = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [missionRes, corridorRes] = await Promise.all([
        api.get('/missions', { params: { mission_type: 'ogi_survey', limit: 100 } }),
        satelliteBoundariesApi.getAll(),
      ]);
      const all: FacilityMission[] = missionRes.data?.missions ?? missionRes.data?.data?.missions ?? [];
      setMissions(
        all.filter(
          (m) =>
            m.metadata?.vertical === 'oil_gas' &&
            m.metadata?.ai_analysis_status === 'complete' &&
            (m.metadata?.thermal_anomaly_c != null || m.metadata?.flare_intensity_score != null),
        ),
      );
      const allCorridors: Corridor[] = corridorRes.data?.boundaries ?? [];
      setCorridors(allCorridors.filter((c) => c.metadata?.vertical === 'oil_gas'));
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = missions.filter((m) => {
    if (filterCorridor && m.metadata?.corridor_name !== filterCorridor) return false;
    if (filterAsset && m.metadata?.asset_type !== filterAsset) return false;
    return true;
  });

  // ── Aggregate stats ──
  const critCount  = missions.filter((m) => m.metadata?.severity === 'critical').length;
  const avgThermal = missions.length
    ? missions.reduce((s, m) => s + (m.metadata?.thermal_anomaly_c ?? 0), 0) / missions.length
    : 0;
  const maxFlare   = missions.length
    ? Math.max(...missions.map((m) => m.metadata?.flare_intensity_score ?? 0))
    : 0;

  // Asset type breakdown
  const assetBreakdown = missions.reduce<Record<string, number>>((acc, m) => {
    const t = m.metadata?.asset_type ?? 'unknown';
    acc[t] = (acc[t] ?? 0) + 1;
    return acc;
  }, {});

  const SELECT_CLS = 'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Flame className="w-7 h-7 text-orange-500" /> Facility Safety &amp; Flaring
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Thermal baseline tracking, flare intensity scoring, and asset safety heatmaps
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filterCorridor} onChange={(e) => setFilterCorridor(e.target.value)} className={SELECT_CLS}>
            <option value="">All Corridors</option>
            {corridors.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          <select value={filterAsset} onChange={(e) => setFilterAsset(e.target.value)} className={SELECT_CLS}>
            <option value="">All Asset Types</option>
            {Object.entries(ASSET_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* ── Stats ── */}
      {missions.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Facilities Monitored',    value: missions.length,          colour: 'text-gray-900 dark:text-white' },
            { label: 'Critical Thermal Events', value: critCount,                colour: 'text-red-700 dark:text-red-400' },
            { label: 'Avg Thermal Anomaly',     value: `+${avgThermal.toFixed(1)}°C`, colour: 'text-orange-700 dark:text-orange-400' },
            { label: 'Peak Flare Score',        value: `${maxFlare}/100`,        colour: 'text-amber-700 dark:text-amber-400' },
          ].map(({ label, value, colour }) => (
            <Card key={label} padding="sm">
              <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
              <p className={`text-2xl font-bold mt-1 ${colour}`}>{value}</p>
            </Card>
          ))}
        </div>
      )}

      {/* ── Asset type breakdown ── */}
      {Object.keys(assetBreakdown).length > 0 && (
        <Card padding="sm">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-1.5">
            <ShieldAlert className="w-4 h-4 text-orange-500" /> Asset Safety Heatmap — Events by Type
          </p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(assetBreakdown).map(([type, count]) => (
              <div
                key={type}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800"
              >
                <Thermometer className="w-4 h-4 text-orange-500 flex-shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-orange-800 dark:text-orange-300">{ASSET_LABELS[type] ?? type}</p>
                  <p className="text-xs text-orange-600 dark:text-orange-400">{count} event{count !== 1 ? 's' : ''}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── Mission list ── */}
      <Card padding="none">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {filtered.length} thermal event{filtered.length !== 1 ? 's' : ''}
          </p>
          {(filterCorridor || filterAsset) && (
            <button
              onClick={() => { setFilterCorridor(''); setFilterAsset(''); }}
              className="text-xs text-gray-400 hover:text-gray-600 underline"
            >
              Clear filters
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-14"><Spinner size="lg" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-14">
            <Flame className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">
              {missions.length === 0 ? 'No thermal events detected yet' : 'No events match the current filters'}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {missions.length === 0
                ? 'Complete OGI/thermal surveys in Flight Data & Analysis to populate this page'
                : 'Try different filter values'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.map((m) => {
              const isExpanded = expandedId === m.id;
              const flareScore = m.metadata?.flare_intensity_score;
              return (
                <div key={m.id}>
                  <button
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 text-left"
                    onClick={() => setExpandedId(isExpanded ? null : m.id)}
                  >
                    <Flame className="w-4 h-4 flex-shrink-0 text-orange-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{m.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {m.metadata?.corridor_name ?? '—'}
                        {m.metadata?.asset_type ? ` · ${ASSET_LABELS[m.metadata.asset_type] ?? m.metadata.asset_type}` : ''}
                        {' · '}{new Date(m.created_at).toLocaleDateString('en-GB')}
                      </p>
                      {flareScore != null && <FlareGauge score={flareScore} />}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {m.metadata?.thermal_anomaly_c != null && (
                        <span className="text-xs font-mono font-bold text-red-600 dark:text-red-400">
                          +{m.metadata.thermal_anomaly_c}°C
                        </span>
                      )}
                      {m.metadata?.severity && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
                          m.metadata.severity === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                          m.metadata.severity === 'high'     ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' :
                          'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                        }`}>
                          {m.metadata.severity}
                        </span>
                      )}
                    </div>
                    {isExpanded
                      ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  </button>

                  {isExpanded && (
                    <div className="px-5 pb-5 pt-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 space-y-4">

                      {/* Thermal metrics */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                          { label: 'Thermal Anomaly',     value: m.metadata?.thermal_anomaly_c != null ? `+${m.metadata.thermal_anomaly_c}°C above baseline` : '—', icon: Thermometer },
                          { label: 'Baseline Temp',       value: m.metadata?.baseline_temp_c != null ? `${m.metadata.baseline_temp_c}°C` : '—',                    icon: BarChart2 },
                          { label: 'Peak Temp',           value: m.metadata?.peak_temp_c != null ? `${m.metadata.peak_temp_c}°C` : '—',                            icon: AlertTriangle },
                          { label: 'Flare Radiative Pwr', value: m.metadata?.flare_radiative_power_mw != null ? `${m.metadata.flare_radiative_power_mw} MW` : '—', icon: Flame },
                        ].map(({ label, value, icon: Icon }) => (
                          <div key={label} className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3">
                            <div className="flex items-center gap-1.5 mb-1">
                              <Icon className="w-3.5 h-3.5 text-orange-500" />
                              <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                            </div>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">{value}</p>
                          </div>
                        ))}
                      </div>

                      {/* Flare intensity bar */}
                      {flareScore != null && (
                        <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-3">
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-1">
                            <Flame className="w-3.5 h-3.5 text-orange-500" /> Flare Intensity Score
                          </p>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-3 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  flareScore >= 80 ? 'bg-red-500' :
                                  flareScore >= 60 ? 'bg-orange-500' :
                                  flareScore >= 30 ? 'bg-amber-400' :
                                  'bg-green-500'
                                }`}
                                style={{ width: `${Math.min(100, flareScore)}%` }}
                              />
                            </div>
                            <span className="text-sm font-bold text-gray-900 dark:text-white w-10 text-right">
                              {flareScore}/100
                            </span>
                          </div>
                        </div>
                      )}

                      {/* AI diagnosis */}
                      {m.metadata?.diagnosis && (
                        <div className="rounded-lg bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800 p-3">
                          <p className="text-xs font-semibold text-orange-700 dark:text-orange-400 mb-1">AI Thermal Diagnosis</p>
                          <p className="text-sm text-gray-700 dark:text-gray-300">{m.metadata.diagnosis}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default FacilitySafetyPage;

// Made with Bob
